import { CANVAS_ELEMENT_TYPES } from "../nativeCanvasElements";
import {
  getArrowPoints,
  getElementBounds,
  getElementFrame,
  isPointInBox,
} from "./elementBounds";
import { getArrowHeadPoints } from "./arrowGeometry";

export const DEFAULT_HIT_PADDING = 6;

const TRANSPARENT_FILL = "transparent";

const getFillColor = (element) => element.style?.fill ?? element.fill;

const hasVisibleFill = (element) => {
  const fill = getFillColor(element);
  return fill !== undefined && fill !== TRANSPARENT_FILL;
};

/**
 * @param {{ point: { x: number, y: number }, start: { x: number, y: number }, end: { x: number, y: number } }} params
 * @returns {number}
 */
export const getDistanceToSegment = ({ end, point, start }) => {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const lengthSquared = segmentX * segmentX + segmentY * segmentY;
  if (!lengthSquared) return Math.hypot(point.x - start.x, point.y - start.y);

  const projection =
    ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) /
    lengthSquared;
  const clamped = Math.min(Math.max(projection, 0), 1);

  return Math.hypot(
    point.x - (start.x + clamped * segmentX),
    point.y - (start.y + clamped * segmentY)
  );
};

const getPolygonEdges = (vertices) =>
  vertices.map((vertex, index) => ({
    end: vertices[(index + 1) % vertices.length],
    start: vertex,
  }));

const isPointNearPolygonBorder = ({ padding, point, vertices }) =>
  getPolygonEdges(vertices).some(
    (edge) => getDistanceToSegment({ ...edge, point }) <= padding
  );

const getDiamondVertices = (frame) => {
  const centerX = frame.x + frame.width / 2;
  const centerY = frame.y + frame.height / 2;

  return [
    { x: centerX, y: frame.y },
    { x: frame.x + frame.width, y: centerY },
    { x: centerX, y: frame.y + frame.height },
    { x: frame.x, y: centerY },
  ];
};

const isPointInDiamond = ({ frame, point }) => {
  const halfWidth = frame.width / 2;
  const halfHeight = frame.height / 2;
  const deltaX = Math.abs(point.x - (frame.x + halfWidth));
  const deltaY = Math.abs(point.y - (frame.y + halfHeight));

  return deltaX / halfWidth + deltaY / halfHeight <= 1;
};

const getNormalizedEllipseRadius = ({ frame, point }) => {
  const deltaX = point.x - (frame.x + frame.width / 2);
  const deltaY = point.y - (frame.y + frame.height / 2);
  const radiusX = Math.max(frame.width / 2, 1);
  const radiusY = Math.max(frame.height / 2, 1);

  return Math.hypot(deltaX / radiusX, deltaY / radiusY);
};

const hitTestRectangle = ({ element, padding, point }) => {
  const frame = getElementFrame(element);
  if (!isPointInBox({ box: frame, padding, point })) return false;
  if (hasVisibleFill(element)) return true;

  return !isPointInBox({ box: frame, padding: -padding, point });
};

const hitTestDiamond = ({ element, padding, point }) => {
  const frame = getElementFrame(element);
  const vertices = getDiamondVertices(frame);
  if (isPointNearPolygonBorder({ padding, point, vertices })) return true;

  return hasVisibleFill(element) && isPointInDiamond({ frame, point });
};

const hitTestEllipse = ({ element, padding, point }) => {
  const frame = getElementFrame(element);
  const normalizedRadius = getNormalizedEllipseRadius({ frame, point });
  const minRadius = Math.max(Math.min(frame.width, frame.height) / 2, 1);
  const isNearBorder = Math.abs(normalizedRadius - 1) * minRadius <= padding;
  if (isNearBorder) return true;

  return hasVisibleFill(element) && normalizedRadius <= 1;
};

const hitTestArrow = ({ element, padding, point }) => {
  const points = getArrowPoints(element);
  const { leftWing, rightWing, tip } = getArrowHeadPoints(points);
  const segments = [
    { end: tip, start: { x: points.x1, y: points.y1 } },
    { end: tip, start: leftWing },
    { end: rightWing, start: tip },
  ];

  return segments.some(
    ({ end, start }) => getDistanceToSegment({ end, point, start }) <= padding
  );
};

const hitTestPath = ({ element, padding, point }) => {
  const points = Array.isArray(element.points) ? element.points : [];
  if (points.length === 1) {
    return Math.hypot(point.x - points[0].x, point.y - points[0].y) <= padding;
  }

  return points.some((start, index) => {
    const end = points[index + 1];
    if (!end) return false;
    return getDistanceToSegment({ end, point, start }) <= padding;
  });
};

const HIT_TESTS_BY_TYPE = {
  [CANVAS_ELEMENT_TYPES.ARROW]: hitTestArrow,
  [CANVAS_ELEMENT_TYPES.DIAMOND]: hitTestDiamond,
  [CANVAS_ELEMENT_TYPES.ELLIPSE]: hitTestEllipse,
  [CANVAS_ELEMENT_TYPES.PATH]: hitTestPath,
  [CANVAS_ELEMENT_TYPES.RECTANGLE]: hitTestRectangle,
};

/**
 * @param {{ element: object, padding?: number, point: { x: number, y: number } }} params
 * @returns {boolean}
 */
export const hitTestElement = ({ element, padding = DEFAULT_HIT_PADDING, point }) => {
  const hitTest = HIT_TESTS_BY_TYPE[element.type];
  if (hitTest) return hitTest({ element, padding, point });

  return isPointInBox({ box: getElementBounds(element), padding, point });
};

/**
 * Topmost element under the pointer, or null.
 * @param {{ elements: Array<object>, padding?: number, point: { x: number, y: number } }} params
 * @returns {object | null}
 */
export const getElementAtPoint = ({ elements, padding, point }) => {
  for (let index = elements.length - 1; index >= 0; index -= 1) {
    const element = elements[index];
    if (hitTestElement({ element, padding, point })) return element;
  }

  return null;
};
